import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertBar, AlertBarButton } from '@/components/AlertBar';
import { formatBrokerTime } from '@/lib/time';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'motion/react';
import { ArrowDown2, ArrowUp2 } from 'iconsax-reactjs';
import { alertReached, type PriceAlert, type PriceQuote } from '@pipyar/core';
import { useI18n } from '@/lib/i18n';
import { useAlertSound } from '@/lib/useAlertSound';
import { closeNotification, closeNotificationsExcept, showNotification } from '@/lib/notify';
import { ALERTS_KEY, ackAlert, claimAlert, useAlerts, useQuotes } from './api';

/** Pause between repeats of the alert sound. */
const SOUND_GAP_MS = 1_600;

/** Snooze lengths offered on the card, in minutes. */
const SNOOZE_MINUTES = [1, 2, 3, 5, 10] as const;

/** Most cards shown at once; the rest ring too, but stay off screen. */
const MAX_CARDS = 3;

/**
 * Snoozes, kept so a page reload does not lose them.
 *
 * localStorage rather than the database: a snooze silences THIS device. Held
 * on the server it would wake every open tab and phone at once. Keyed by the
 * firing (id + triggeredAt), so re-armed and fired again is a fresh alarm.
 */
const SNOOZE_STORAGE_KEY = 'pipyar-price-alert-snoozes-v2';

type Snoozes = Record<string, number>;

function readSnoozes(): Snoozes {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(SNOOZE_STORAGE_KEY) ?? '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Snoozes) : {};
  } catch {
    return {};
  }
}

function writeSnoozes(s: Snoozes): void {
  try {
    if (Object.keys(s).length === 0) localStorage.removeItem(SNOOZE_STORAGE_KEY);
    else localStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* private mode — the snooze just will not survive a reload */
  }
}

const firingKey = (a: PriceAlert) => `${a.id}:${a.triggeredAt}`;

/** Whether price is still on the far side of the line it crossed. */
function stillBeyond(a: PriceAlert, q: PriceQuote): boolean {
  return a.direction === 'above' ? q.price >= a.price : q.price <= a.price;
}

/**
 * Price alerts, from any screen: checks armed lines against the live price,
 * and rings for the ones that have fired.
 *
 * The alarm is STATE, not an event. A fired alert rings while all of these
 * hold, and stops by itself the moment one does not:
 *
 *   - it has not been stopped (✕ on its card, saved on the server),
 *   - it is still enabled and has not been deleted or re-armed,
 *   - price is still beyond the line (back across it = silent until it
 *     crosses again),
 *   - this device has not snoozed it.
 *
 * Because it is derived from saved state rather than held in the page, a
 * reload brings the alarm straight back. Browsers do not allow sound before
 * the first click or key press on a freshly loaded page, so after a reload
 * the card and notification return at once and the sound with that first
 * interaction (see lib/sound.ts).
 *
 * Detecting the crossing is still CLAIMED on the server first, so it is
 * recorded once however many tabs see it.
 */
export function PriceAlertWatcher() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const alerts = useAlerts();
  const { play, prefs } = useAlertSound('price');
  const playRef = useRef(play);
  playRef.current = play;
  // Read when an alarm starts, so changing it mid-alarm does not restart one.
  const repeatRef = useRef(prefs.priceRepeat);
  repeatRef.current = prefs.priceRepeat;

  const all = useMemo(() => alerts.data ?? [], [alerts.data]);
  const armed = useMemo(() => all.filter((a) => a.enabled && a.triggeredAt === null), [all]);
  /** Fired and not stopped: these have a card. */
  const live = useMemo(
    () =>
      all
        .filter((a) => a.enabled && a.triggeredAt !== null && a.acknowledgedAt === null)
        .sort((a, b) => (b.triggeredAt ?? 0) - (a.triggeredAt ?? 0)),
    [all],
  );

  const symbols = useMemo(
    () => [...new Set([...armed, ...live].map((a) => a.symbol))].sort(),
    [armed, live],
  );
  const quotes = useQuotes(symbols);
  const quoteStamp = quotes.map((q) => q.dataUpdatedAt).join(',');
  const bySymbol = useMemo(() => {
    const m = new Map<string, PriceQuote>();
    for (const q of quotes) if (q.data) m.set(q.data.symbol, q.data);
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteStamp]);

  // ---- detect crossings ----

  /** Alerts with a claim in flight, so a slow response is not claimed twice. */
  const claiming = useRef(new Set<number>());
  useEffect(() => {
    for (const alert of armed) {
      const q = bySymbol.get(alert.symbol);
      if (!q || claiming.current.has(alert.id)) continue;

      // The bar's high and low only count if the bar opened after the line
      // was armed; otherwise a re-armed line would fire on a move it already saw.
      const barStartReal = q.barTime - q.storedGmtOffsetSec;
      const useRange = barStartReal >= alert.armedAt;
      const high = useRange ? Math.max(q.high, q.price) : q.price;
      const low = useRange ? Math.min(q.low, q.price) : q.price;
      if (!alertReached(alert, high, low)) continue;

      claiming.current.add(alert.id);
      void claimAlert(alert.id, alert.direction === 'above' ? high : low)
        .then(({ alert: updated }) => {
          // The card follows from the updated alert; nothing else to do here.
          qc.setQueryData<{ alerts: PriceAlert[] }>(ALERTS_KEY, (prev) =>
            prev ? { alerts: prev.alerts.map((a) => (a.id === updated.id ? updated : a)) } : prev,
          );
        })
        .catch(() => {
          /* network blip: the next poll tries again */
        })
        .finally(() => claiming.current.delete(alert.id));
    }
  }, [armed, bySymbol, qc]);

  // ---- snoozes ----

  const [snoozes, setSnoozes] = useState<Snoozes>(readSnoozes);
  /** Bumped per firing when a snooze runs out, so the alarm count restarts. */
  const [wakes, setWakes] = useState<Record<string, number>>({});

  // Drop snoozes for firings that no longer exist, once the list is known.
  useEffect(() => {
    if (!alerts.data) return;
    const keys = new Set(live.map(firingKey));
    setSnoozes((prev) => {
      const next = Object.fromEntries(Object.entries(prev).filter(([k]) => keys.has(k)));
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [alerts.data, live]);
  useEffect(() => writeSnoozes(snoozes), [snoozes]);

  const anySnoozed = Object.keys(snoozes).length > 0;
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!anySnoozed) return;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [anySnoozed]);

  // A snooze that has run out — including while the page was closed — ends.
  useEffect(() => {
    const due = Object.entries(snoozes).filter(([, until]) => until <= nowMs).map(([k]) => k);
    if (due.length === 0) return;
    setSnoozes((prev) => {
      const next = { ...prev };
      for (const k of due) delete next[k];
      return next;
    });
    setWakes((prev) => {
      const next = { ...prev };
      for (const k of due) next[k] = (next[k] ?? 0) + 1;
      return next;
    });
  }, [snoozes, nowMs]);

  // ---- what is ringing ----

  const ringing = live.filter((a) => {
    const q = bySymbol.get(a.symbol);
    return q !== undefined && stillBeyond(a, q) && snoozes[firingKey(a)] === undefined;
  });

  /*
   * A system notification each time an alert STARTS ringing: when it fires,
   * when price crosses back over the line, and once after a reload. Not on
   * every poll, and not when a snooze ends — the card is already on screen.
   */
  const ringingNow = new Set(ringing.map(firingKey));
  const wasRinging = useRef<Set<string>>(new Set());
  const ringingKeys = [...ringingNow].sort().join(',');
  useEffect(() => {
    for (const a of ringing) {
      const k = firingKey(a);
      if (wasRinging.current.has(k) || wakes[k]) continue;
      const q = bySymbol.get(a.symbol)!;
      const dir = t(a.direction === 'above' ? 'alerts.crossedAbove' : 'alerts.crossedBelow');
      void showNotification(
        `${a.symbol} ${dir} ${a.price.toFixed(q.digits)}`,
        `${t('alerts.now')} ${q.price.toFixed(q.digits)}${a.note ? ` · ${a.note}` : ''}`,
        `price-alert-${a.id}`,
      );
    }
    wasRinging.current = ringingNow;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ringingKeys]);

  /*
   * A system notification stays only while its alarm stands: a card that is
   * ringing or snoozed. Stopped, deleted, re-armed, or price back across the
   * line, and it comes down. Swept against what is on screen rather than what
   * this page showed, so a notification left by an earlier page load or
   * stopped on another device is cleared too. Waits until every card has a
   * quote, so a quote still loading is not read as "price went back".
   */
  const keepTags = live
    .filter((a) => {
      const q = bySymbol.get(a.symbol);
      return q === undefined || stillBeyond(a, q);
    })
    .map((a) => `price-alert-${a.id}`)
    .sort()
    .join(',');
  const listsReady = alerts.data !== undefined && live.every((a) => bySymbol.has(a.symbol));
  useEffect(() => {
    if (!listsReady) return;
    void closeNotificationsExcept('price-alert-', new Set(keepTags ? keepTags.split(',') : []));
  }, [keepTags, listsReady]);

  /*
   * Sound: an alarm, not a single ping, repeated as set in Settings → Alert
   * sounds (by default until stopped). The count restarts whenever the set of
   * ringing alerts changes or a snooze runs out; it ends when nothing rings.
   */
  const ringKey = ringing.map((a) => `${firingKey(a)}:${wakes[firingKey(a)] ?? 0}`).join(',');
  useEffect(() => {
    if (ringKey === '') return;
    const limit = repeatRef.current;
    let played = 1;
    playRef.current();
    if (limit === 1) return;
    const id = setInterval(() => {
      if (limit > 0 && played >= limit) {
        clearInterval(id);
        return;
      }
      played += 1;
      playRef.current();
    }, SOUND_GAP_MS);
    return () => clearInterval(id);
  }, [ringKey]);

  // ---- actions ----

  /** Stop: saved on the server, so it holds across reloads and devices. */
  const stop = (a: PriceAlert) => {
    const now = Math.floor(Date.now() / 1000);
    qc.setQueryData<{ alerts: PriceAlert[] }>(ALERTS_KEY, (prev) =>
      prev ? { alerts: prev.alerts.map((x) => (x.id === a.id ? { ...x, acknowledgedAt: now } : x)) } : prev,
    );
    void closeNotification(`price-alert-${a.id}`);
    void ackAlert(a.id)
      .catch(() => {
        /* the next alerts refetch puts the card back if this did not land */
      })
      .finally(() => void qc.invalidateQueries({ queryKey: ALERTS_KEY }));
  };
  const snooze = (a: PriceAlert, minutes: number) =>
    setSnoozes((prev) => ({ ...prev, [firingKey(a)]: Date.now() + minutes * 60_000 }));
  const unsnooze = (a: PriceAlert) => setSnoozes((prev) => ({ ...prev, [firingKey(a)]: Date.now() }));

  return (
    <AnimatePresence>
      {live.slice(0, MAX_CARDS).map((a) => {
        const up = a.direction === 'above';
        const q = bySymbol.get(a.symbol);
        const digits = q?.digits ?? 2;
        const beyond = q ? stillBeyond(a, q) : true;
        const snoozedUntil = snoozes[firingKey(a)];
        const ringingNow = beyond && snoozedUntil === undefined;
        return (
          <AlertBar
            key={firingKey(a)}
            role="alert"
            accent={up ? 'var(--bull)' : 'var(--bear)'}
            icon={up ? <ArrowUp2 size={16} variant="Bold" /> : <ArrowDown2 size={16} variant="Bold" />}
            // A ringing alarm ripples; a silent one (snoozed, or price back
            // across the line) sits still.
            live={ringingNow}
            kind={t('alerts.priceAlert')}
            title={`${a.symbol} ${t(up ? 'alerts.crossedAbove' : 'alerts.crossedBelow')} ${a.price.toFixed(digits)}`}
            subtitle={
              <>
                {!beyond
                  ? t(up ? 'alerts.backBelow' : 'alerts.backAbove')
                  : (a.note || t('alerts.priceAlert'))}
                {a.triggeredAt !== null && (
                  <>
                    {' · '}
                    <span className="tabular" title={t('common.brokerTime')}>
                      {formatBrokerTime(
                        a.triggeredAt,
                        q?.storedGmtOffsetSec ?? 0,
                        t('common.today'),
                      )}
                    </span>
                  </>
                )}
              </>
            }
            valueCaption={t('alerts.now')}
            value={q ? q.price.toFixed(digits) : undefined}
            actions={
              !beyond ? undefined : snoozedUntil !== undefined ? (
                <>
                  <span className="px-2 text-muted-foreground">{t('alerts.snoozed')}</span>
                  <span className="tabular pe-1 font-semibold">{formatCountdown(snoozedUntil - nowMs)}</span>
                  <AlertBarButton onClick={() => unsnooze(a)}>{t('alerts.unsnooze')}</AlertBarButton>
                </>
              ) : (
                <>
                  <span className="px-2 text-muted-foreground">{t('alerts.snooze')}</span>
                  {SNOOZE_MINUTES.map((m) => (
                    <AlertBarButton key={m} onClick={() => snooze(a, m)} label={t('alerts.snoozeFor', { n: m })}>
                      {m}m
                    </AlertBarButton>
                  ))}
                </>
              )
            }
            onClose={() => stop(a)}
            closeLabel={t('alerts.dismiss')}
            closeTitle={t('alerts.stop')}
          />
        );
      })}
    </AnimatePresence>
  );
}

/** m:ss for the snooze countdown. */
function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
